import { resolveParallel, resolveSerial } from '@/helpers/promises'
import { resolveGuide } from '@/resolve-guide'
import { resolveReference } from '@/resolve-reference'
import type { ConfigFileGetter } from '@/types'
import type { ConfigFileSpec, ConfigFolderGetter } from '@/types'
import {
  defaultProject,
  defaultVersion,
  type Logo,
  type GithubProject,
  type Project,
} from '@scalar-org/entities/project'
import type { ConfigGuide, ConfigReference, ScalarConfig } from '@/schema'
import type { Theme } from '@scalar-org/entities/theme'
import { resolveTheme } from '@/resolve-theme'

/**
 * Resolve a valid Scalar project entity from a Scalar config file.
 *
 * Will return the project entity, all file strings, and a populated theme
 */
export async function resolveSyncProject<F extends ConfigFileSpec>({
  config,
  fileGetter,
  folderGetter,
  parallel = false,
  gitProject,
  theme: _theme,
}: {
  config: ScalarConfig
  fileGetter: ConfigFileGetter<F>
  folderGetter: ConfigFolderGetter<F>
  parallel?: boolean
  gitProject?: GithubProject
  theme?: Theme
}): Promise<{
  /** The resolved project entity. Will share the uid of any provided gitProject */
  project: Project
  /** Map of page uids to page file content */
  documents: Record<string, string>
  /** Map of reference uids to reference file content */
  references: Record<string, string>
  /** Map of page and reference uids to their file path */
  documentPaths: Record<string, string>
  /** The resolved theme entity. Includes all CSS from the config files */
  theme: Theme
}> {
  /** Record of pageUids to page file content  */
  const documents: Record<string, string> = {}
  /** Record of referenceUids to reference file content  */
  const references: Record<string, string> = {}

  const documentPaths: Record<string, string> = {}

  const theme = await resolveTheme({
    config,
    theme: _theme,
    fileGetter,
  })

  const baseProject = defaultProject({
    ...(gitProject ?? {}),
  })

  const baseVersion = defaultVersion({
    footer: config.siteConfig?.footer || '',
    headScript: config.siteConfig?.headScript || '',
    bodyScript: config.siteConfig?.bodyScript || '',
    guides: {},
    references: {},
  })

  const project: Project = {
    ...baseProject,
    versions: {
      [baseVersion.uid]: baseVersion,
    },
    website: {
      ...baseProject.website,
      customUrl: config.customDomain || '',
      subdomainPrefix: config.subdomain,
      favicon: config.siteMeta?.favicon,
      ogImage: config.siteMeta?.ogImage,
      title: config.siteMeta?.title,
      description: config.siteMeta?.description,
      routing: {
        guidePathPattern: config?.siteConfig?.routing?.guidePathPattern || '',
        referencePathPattern:
          config?.siteConfig?.routing?.referencePathPattern || '',
      },
      redirects: config.siteConfig?.routing?.redirects || [],
    },
    activeThemeId: theme.uid,
    activeDeployment: null,
    activeVersionId: baseVersion.uid,
    lastPublishedUid: null,
    logo: mapSiteConfigToLogo(config),
  } satisfies Project

  // ---------------------------------------------------------------------------
  async function getReference(entry: ConfigReference) {
    const { reference, content } = await resolveReference({
      config: entry,
      fileGetter,
      root: config.root ?? '',
    })

    baseVersion.references[reference.uid] = reference

    // Set reference content if live link is not specified
    if ('path' in entry && content) {
      references[reference.uid] = content
      documentPaths[reference.uid] = entry.path
    }
  }

  async function getGuide(entry: ConfigGuide) {
    const {
      guide,
      documents: guideDocuments,
      documentPaths: guideDocumentPaths,
    } = await resolveGuide({
      config: entry,
      fileGetter,
      folderGetter,
      parallel,
      root: config.root ?? '',
    })

    baseVersion.guides[guide.uid] = guide
    Object.keys(guideDocuments).forEach((g) => {
      documents[g] = guideDocuments[g]
    })
    Object.keys(guideDocumentPaths).forEach((g) => {
      documentPaths[g] = guideDocumentPaths[g]
    })
  }

  /**
   * Be cautious with the contents of the promise functions.
   *
   * Order is indeterminate so no actions that require a specific order can be executed (pushing to an array)
   */
  const resolver = parallel ? resolveParallel : resolveSerial

  await resolver([
    ...config.references.map(getReference),
    ...config.guides.map(getGuide),
  ])

  return {
    project,
    documents,
    references,
    documentPaths,
    theme,
  }
}

/** Map the site config logo to the project logo */
export const mapSiteConfigToLogo = (config: ScalarConfig): Logo => {
  if (typeof config.siteConfig?.logo === 'string')
    return {
      darkMode: config.siteConfig?.logo,
      lightMode: config.siteConfig?.logo,
    }
  return {
    darkMode: config.siteConfig?.logo?.darkMode ?? '',
    lightMode: config.siteConfig?.logo?.lightMode ?? '',
  }
}
